import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import { 
  Users, Plus, Edit2, Trash2, Save, X, Upload, Mail, Phone,
  User, Shield, Award, Heart, Briefcase, ChevronDown, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Department options
const DEPARTMENTS = [
  { value: 'board', label: 'Board of Directors' },
  { value: 'coaching', label: 'Coaching Staff' },
  { value: 'operations', label: 'Operations' },
  { value: 'volunteer', label: 'Volunteer Leadership' }
];

// Role options by department
const ROLES_BY_DEPT = {
  board: [
    { value: 'president', label: 'President' },
    { value: 'vice_president', label: 'Vice President' },
    { value: 'secretary', label: 'Secretary' },
    { value: 'treasurer', label: 'Treasurer' },
    { value: 'board_member', label: 'Board Member' }
  ],
  coaching: [
    { value: 'head_coach', label: 'Head Coach' },
    { value: 'assistant_coach', label: 'Assistant Coach' },
    { value: 'flag_coach', label: 'Flag Football Coach' },
    { value: 'cheer_coach', label: 'Cheer Coach' },
    { value: 'coordinator', label: 'Coordinator' }
  ],
  operations: [
    { value: 'equipment_manager', label: 'Equipment Manager' },
    { value: 'registrar', label: 'Registrar' },
    { value: 'team_mom', label: 'Team Mom' },
    { value: 'other', label: 'Other' }
  ],
  volunteer: [
    { value: 'volunteer_coordinator', label: 'Volunteer Coordinator' },
    { value: 'other', label: 'Other' }
  ]
};

// Age groups for coaches - updated to match division structure
const AGE_GROUPS = [
  { value: 'Flag Football — Little Chiefs (5–7U)', label: 'Flag Football — Little Chiefs (5–7U)' },
  { value: 'Tackle Football — Tiny Chiefs (7–8U)', label: 'Tackle Football — Tiny Chiefs (7–8U)' },
  { value: 'Tackle Football — Mighty Chiefs (9U)', label: 'Tackle Football — Mighty Chiefs (9U)' },
  { value: 'Tackle Football — Junior Chiefs (10U)', label: 'Tackle Football — Junior Chiefs (10U)' },
  { value: 'Tackle Football — Varsity Chiefs (11U)', label: 'Tackle Football — Varsity Chiefs (11U)' },
  { value: 'Tackle Football — Senior Chiefs (12U)', label: 'Tackle Football — Senior Chiefs (12U)' },
  { value: 'Tackle Football — Elite Chiefs (13U)', label: 'Tackle Football — Elite Chiefs (13U)' },
  { value: 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)', label: 'Cheer — The Lady Chiefs: Cheer Sparks (5–8)' },
  { value: 'Cheer — The Lady Chiefs: Cheer Stars (9–11)', label: 'Cheer — The Lady Chiefs: Cheer Stars (9–11)' },
  { value: 'Cheer — The Lady Chiefs: Cheer Elite (12–14)', label: 'Cheer — The Lady Chiefs: Cheer Elite (12–14)' }
];

const AdminStaffPage = () => {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('staff');
  
  // Modal states
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingDept, setEditingDept] = useState(null);
  
  // Form states
  const [staffForm, setStaffForm] = useState({
    name: '', title: '', email: '', phone: '', department: 'coaching',
    role: 'head_coach', age_group: '', photo_url: '', bio: '', is_active: true
  });
  const [deptForm, setDeptForm] = useState({
    name: '', email: '', description: '', contact_staff_id: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [staffRes, deptRes] = await Promise.all([
        fetch(`${API}/api/staff?active_only=false`, { credentials: 'include' }),
        fetch(`${API}/api/departments`, { credentials: 'include' })
      ]);

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        setStaff(staffData.staff || []);
      }
      if (deptRes.ok) {
        const deptData = await deptRes.json();
        setDepartments(deptData.departments || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setStaffForm(prev => ({ ...prev, photo_url: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  // Staff CRUD
  const openStaffModal = (member = null) => {
    if (member) {
      setEditingStaff(member);
      setStaffForm({
        name: member.name || '',
        title: member.title || '',
        email: member.email || '',
        phone: member.phone || '',
        department: member.department || 'coaching',
        role: member.role || 'head_coach',
        age_group: member.age_group || '',
        photo_url: member.photo_url || '',
        bio: member.bio || '',
        is_active: member.is_active !== false
      });
    } else {
      setEditingStaff(null);
      setStaffForm({
        name: '', title: '', email: '', phone: '', department: 'coaching',
        role: 'head_coach', age_group: '', photo_url: '', bio: '', is_active: true
      });
    }
    setShowStaffModal(true);
  };

  const saveStaff = async () => {
    if (!staffForm.name || !staffForm.title) {
      toast.error('Name and title are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingStaff 
        ? `${API}/api/staff/${editingStaff.staff_id}`
        : `${API}/api/staff`;
      
      const res = await fetch(url, {
        method: editingStaff ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(staffForm)
      });

      if (res.ok) {
        toast.success(editingStaff ? 'Staff member updated' : 'Staff member added');
        setShowStaffModal(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save staff member');
    } finally {
      setSaving(false);
    }
  };

  const deleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to remove this staff member?')) return;
    
    setDeleting(staffId);
    try {
      const res = await fetch(`${API}/api/staff/${staffId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Staff member removed');
        fetchData();
      } else {
        toast.error('Failed to remove staff member');
      }
    } catch (error) {
      toast.error('Failed to remove staff member');
    } finally {
      setDeleting(null);
    }
  };

  // Department CRUD
  const openDeptModal = (dept = null) => {
    if (dept) {
      setEditingDept(dept);
      setDeptForm({
        name: dept.name || '',
        email: dept.email || '',
        description: dept.description || '',
        contact_staff_id: dept.contact_staff_id || ''
      });
    } else {
      setEditingDept(null);
      setDeptForm({ name: '', email: '', description: '', contact_staff_id: '' });
    }
    setShowDeptModal(true);
  };

  const saveDept = async () => {
    if (!deptForm.name || !deptForm.email) {
      toast.error('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingDept 
        ? `${API}/api/departments/${editingDept.department_id}`
        : `${API}/api/departments`;
      
      const res = await fetch(url, {
        method: editingDept ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(deptForm)
      });

      if (res.ok) {
        toast.success(editingDept ? 'Department updated' : 'Department added');
        setShowDeptModal(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const deleteDept = async (deptId) => {
    if (!window.confirm('Are you sure you want to remove this department contact?')) return;
    
    setDeleting(deptId);
    try {
      const res = await fetch(`${API}/api/departments/${deptId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Department contact removed');
        fetchData();
      } else {
        toast.error('Failed to remove department');
      }
    } catch (error) {
      toast.error('Failed to remove department');
    } finally {
      setDeleting(null);
    }
  };

  // Group staff by department
  const groupedStaff = staff.reduce((acc, member) => {
    const dept = member.department || 'other';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(member);
    return acc;
  }, {});

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-64" />
            <div className="h-64 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Staff Directory</h1>
              <p className="text-gray-500 text-sm">Add and manage staff members & department contacts</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
          <button
            onClick={() => setActiveTab('staff')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'staff' 
                ? 'bg-white text-navy shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Staff Members ({staff.length})
          </button>
          <button
            onClick={() => setActiveTab('departments')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'departments' 
                ? 'bg-white text-navy shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Department Contacts ({departments.length})
          </button>
        </div>

        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <div>
            <div className="flex justify-end mb-4">
              <Button onClick={() => openStaffModal()} className="bg-navy hover:bg-navy/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Staff Member
              </Button>
            </div>

            {staff.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-gray-500 font-medium">No staff members yet</h3>
                <p className="text-gray-400 text-sm mb-4">Add your first staff member to get started</p>
                <Button onClick={() => openStaffModal()} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Staff Member
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {DEPARTMENTS.map(dept => (
                  groupedStaff[dept.value]?.length > 0 && (
                    <div key={dept.value} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900">{dept.label}</h3>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {groupedStaff[dept.value].map(member => (
                          <div 
                            key={member.staff_id} 
                            className={`p-4 flex items-center justify-between ${!member.is_active ? 'opacity-50' : ''}`}
                          >
                            <div className="flex items-center space-x-4">
                              {member.photo_url ? (
                                <img 
                                  src={member.photo_url} 
                                  alt={member.name}
                                  className="w-12 h-12 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                                  <User className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <h4 className="font-medium text-gray-900">{member.name}</h4>
                                  {!member.is_active && (
                                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inactive</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{member.title}</p>
                                {member.age_group && (
                                  <span className="text-xs text-navy bg-gold/20 px-2 py-0.5 rounded mt-1 inline-block">
                                    {member.age_group}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openStaffModal(member)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red hover:text-red hover:bg-red/10"
                                onClick={() => deleteStaff(member.staff_id)}
                                disabled={deleting === member.staff_id}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        )}

        {/* Departments Tab */}
        {activeTab === 'departments' && (
          <div>
            <div className="flex justify-end mb-4">
              <Button onClick={() => openDeptModal()} className="bg-navy hover:bg-navy/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Department Contact
              </Button>
            </div>

            {departments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
                <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-gray-500 font-medium">No department contacts yet</h3>
                <p className="text-gray-400 text-sm mb-4">Add department emails for easy contact</p>
                <Button onClick={() => openDeptModal()} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Department
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {departments.map(dept => (
                    <div key={dept.department_id} className="p-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{dept.name}</h4>
                        <p className="text-sm text-light-blue">{dept.email}</p>
                        {dept.description && (
                          <p className="text-sm text-gray-500 mt-1">{dept.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openDeptModal(dept)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red hover:text-red hover:bg-red/10"
                          onClick={() => deleteDept(dept.department_id)}
                          disabled={deleting === dept.department_id}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Staff Modal */}
        {showStaffModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">
                    {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
                  </h2>
                  <button onClick={() => setShowStaffModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Photo Upload */}
                <div className="flex items-center space-x-4">
                  <div 
                    className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer border-2 border-dashed border-gray-300 hover:border-navy transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {staffForm.photo_url ? (
                      <img src={staffForm.photo_url} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload Photo
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">Max 2MB, JPG/PNG</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <Input
                    value={staffForm.name}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <Input
                    value={staffForm.title}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Head Coach - Mighty Chiefs"
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    value={staffForm.department}
                    onChange={(e) => {
                      const dept = e.target.value;
                      setStaffForm(prev => ({ 
                        ...prev, 
                        department: dept,
                        role: ROLES_BY_DEPT[dept]?.[0]?.value || 'other'
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-transparent"
                  >
                    {DEPARTMENTS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={staffForm.role}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-transparent"
                  >
                    {(ROLES_BY_DEPT[staffForm.department] || []).map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Age Group (for coaches) */}
                {staffForm.department === 'coaching' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Division/Age Group</label>
                    <select
                      value={staffForm.age_group}
                      onChange={(e) => setStaffForm(prev => ({ ...prev, age_group: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-transparent"
                    >
                      <option value="">Select division...</option>
                      {AGE_GROUPS.map(ag => (
                        <option key={ag.value} value={ag.value}>{ag.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <Input
                    type="email"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@parkcitychiefs.org"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <Input
                    type="tel"
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  <textarea
                    value={staffForm.bio}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Brief bio or background..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-transparent resize-none"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={staffForm.is_active}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-gray-300 text-navy focus:ring-navy"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Active (visible in directory)</label>
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowStaffModal(false)}>
                  Cancel
                </Button>
                <Button onClick={saveStaff} disabled={saving} className="bg-navy hover:bg-navy/90">
                  {saving ? 'Saving...' : (editingStaff ? 'Update' : 'Add Staff Member')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Department Modal */}
        {showDeptModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-md">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">
                    {editingDept ? 'Edit Department Contact' : 'Add Department Contact'}
                  </h2>
                  <button onClick={() => setShowDeptModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department Name *</label>
                  <Input
                    value={deptForm.name}
                    onChange={(e) => setDeptForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Registration, Equipment"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <Input
                    type="email"
                    value={deptForm.email}
                    onChange={(e) => setDeptForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="registration@parkcitychiefs.org"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={deptForm.description}
                    onChange={(e) => setDeptForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What this department handles..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person (Optional)</label>
                  <select
                    value={deptForm.contact_staff_id}
                    onChange={(e) => setDeptForm(prev => ({ ...prev, contact_staff_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-navy focus:border-transparent"
                  >
                    <option value="">No specific contact</option>
                    {staff.filter(s => s.is_active).map(s => (
                      <option key={s.staff_id} value={s.staff_id}>{s.name} - {s.title}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowDeptModal(false)}>
                  Cancel
                </Button>
                <Button onClick={saveDept} disabled={saving} className="bg-navy hover:bg-navy/90">
                  {saving ? 'Saving...' : (editingDept ? 'Update' : 'Add Department')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminStaffPage;
